// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/sidebar/AppSidebar";
import { MobileSidebarCloser } from "@/components/sidebar/MobileSidebarCloser";
import { SuspenseShellLoader } from "@/components/loading";
import { DashboardTopBar } from "./DashboardTopBar";
import { PendingPlanChangeBanner } from "@/components/PendingPlanChangeBanner";
import { ModuleAccessGuard } from "@/components/ModuleAccessGuard";
import { useAuth } from "@/contexts/AuthContext";
import { applyUiSkin, resolveUiSkin } from "@/lib/uiSkin";

export function DashboardShell() {
  const { profile } = useAuth();

  useEffect(() => {
    applyUiSkin(resolveUiSkin(profile?.id));
  }, [profile?.id]);

  return (
    <SidebarProvider defaultOpen>
      <MobileSidebarCloser />
      <AppSidebar />
      <SidebarInset className="flex min-h-svh min-w-0 flex-col bg-background">
        <DashboardTopBar />
        <PendingPlanChangeBanner />
        <main className="flex flex-1 min-w-0 flex-col overflow-x-hidden overflow-y-auto">
          {/* Guard lives inside the shell so tier locks render in place
              (sidebar + top bar stay) instead of ejecting to /pricing. */}
          <ModuleAccessGuard>
            <Suspense fallback={<SuspenseShellLoader />}>
              <Outlet />
            </Suspense>
          </ModuleAccessGuard>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
